import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

// 配置axios基础URL - 使用相对路径自动适配当前域名
const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}/api`;
  }
  return '/api';
};

axios.defaults.baseURL = getBaseURL();
axios.defaults.timeout = 30000;

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      // 初始化函数，从localStorage恢复token并设置axios headers
      initialize: async () => {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        
        if (token) {
          // 设置axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          if (userStr) {
            try {
              const user = JSON.parse(userStr);
              set({
                user,
                token,
                isAuthenticated: true
              });
            } catch (error) {
              console.error('解析用户信息失败:', error);
              // 清除无效数据
              localStorage.removeItem('user');
              // 只有token，尝试通过verifyToken获取用户信息
              set({
                token,
                isAuthenticated: false
              });
            }
          } else {
            // 只有token，没有user，先设置token
            set({
              token,
              isAuthenticated: false
            });
          }
        } else {
          // 没有token，清除所有认证信息
          set({
            user: null,
            token: null,
            isAuthenticated: false
          });
        }
      },

      // 登录
      login: async (phone, password) => {
        try {
          const response = await axios.post('/auth/login', {
            phone,
            password
          });

          if (response.data.success) {
            const { token, user } = response.data;
            
            // 保存到localStorage
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('userId', user.id);
            localStorage.setItem('userRole', user.role);
            
            // 设置axios默认headers
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            set({
              user,
              token,
              isAuthenticated: true
            });

            return { success: true, user };
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          return {
            success: false,
            message: error.response?.data?.message || '登录失败'
          };
        }
      },

      // 注册
      register: async (phone, password, referrerCode = null) => {
        try {
          const response = await axios.post('/auth/register', {
            phone,
            password,
            referrerCode
          });

          if (response.data.success) {
            const { token, user } = response.data;
            
            // 保存到localStorage
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('userId', user.id);
            localStorage.setItem('userRole', user.role);
            
            // 设置axios默认headers
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            set({
              user,
              token,
              isAuthenticated: true
            });

            return { success: true, user };
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          return {
            success: false,
            message: error.response?.data?.message || '注册失败'
          };
        }
      },

      // 验证token
      verifyToken: async () => {
        const { token } = get();
        if (!token) {
          return false;
        }

        try {
          const response = await axios.get('/auth/verify', {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data.success) {
            set({
              user: response.data.user,
              isAuthenticated: true
            });
            return true;
          } else {
            get().logout();
            return false;
          }
        } catch (error) {
          get().logout();
          return false;
        }
      },

      // 静默刷新用户信息：拉取后端权威用户（含 member_level/role/trial_end_date）。
      // 与 verifyToken 的区别：网络失败/异常时【不登出】，保留现有用户，避免冷启动误退登。
      // 用于 App 初始化时，确保首页会员判断拿到的是最新会员等级。
      refreshUser: async () => {
        const token = get().token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
        if (!token) return false;
        try {
          const response = await axios.get('/auth/verify', {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data && response.data.success && response.data.user) {
            const u = response.data.user;
            set({ user: u, token, isAuthenticated: true });
            try {
              localStorage.setItem('user', JSON.stringify(u));
              if (u.id != null) localStorage.setItem('userId', u.id);
              if (u.role != null) localStorage.setItem('userRole', u.role);
            } catch (_) {}
            return true;
          }
          return false;
        } catch (error) {
          // 网络异常时静默失败，保留已有用户
          console.error('refreshUser 失败（保留现有用户）:', error?.message || error);
          return false;
        }
      },

      // 登出
      logout: () => {
        delete axios.defaults.headers.common['Authorization'];
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        localStorage.removeItem('userRole');
        set({
          user: null,
          token: null,
          isAuthenticated: false
        });
      },

      // 更新用户信息
      updateUser: (userData) => {
        set({ user: { ...get().user, ...userData } });
      },

      // 更新用户资料
      updateProfile: async (userData) => {
        try {
          const response = await axios.put('/auth/profile', userData);
          if (response.data.success) {
            // 如果有账户合并，返回完整的响应数据
            if (response.data.merged && response.data.user) {
              set({ user: response.data.user });
              return response.data; // 返回完整响应，包括merged和token
            } else {
              set({ user: { ...get().user, ...userData } });
              return { success: true, message: response.data.message || '用户信息更新成功' };
            }
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          console.error('更新用户资料错误:', error);
          return {
            success: false,
            message: error.response?.data?.message || '更新失败'
          };
        }
      },

      // 修改密码
      changePassword: async (currentPassword, newPassword) => {
        try {
          const response = await axios.put('/auth/change-password', {
            currentPassword,
            newPassword
          });
          return response.data;
        } catch (error) {
          return {
            success: false,
            message: error.response?.data?.message || '修改密码失败'
          };
        }
      },

      // 微信登录处理
      handleWeChatLogin: async (loginData) => {
        try {
          const { token, user } = loginData;

          // 先设置 token 与 axios header
          localStorage.setItem('token', token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // 小程序传来的 userInfo 只有微信昵称/头像等，缺少 member_level/role/trial_end_date
          // 等会员字段。必须用 token 从后端拉取权威用户信息并覆盖，否则首页做会员判断时
          // 会误判为未升级、提示升级会员（进「我的」触发 verifyToken 后才恢复正常）。
          let finalUser = user || {};
          try {
            const resp = await axios.get('/auth/verify', {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (resp.data && resp.data.success && resp.data.user) {
              finalUser = { ...finalUser, ...resp.data.user };
            }
          } catch (e) {
            // 拉取失败则退回小程序传入的 userInfo（保底可登录）
            console.error('微信登录后拉取权威用户信息失败:', e);
          }

          // 保存到localStorage
          localStorage.setItem('user', JSON.stringify(finalUser));
          if (finalUser.id != null) localStorage.setItem('userId', finalUser.id);
          if (finalUser.role != null) localStorage.setItem('userRole', finalUser.role);

          set({
            user: finalUser,
            token,
            isAuthenticated: true
          });

          return { success: true, user: finalUser };
        } catch (error) {
          return {
            success: false,
            message: '微信登录处理失败'
          };
        }
      },

      // 绑定微信账号
      bindWeChat: async (code) => {
        try {
          const response = await axios.post('/auth/wechat/bind', { code });
          return response.data;
        } catch (error) {
          return {
            success: false,
            message: error.response?.data?.message || '微信账号绑定失败'
          };
        }
      },

      // 解绑微信账号
      unbindWeChat: async () => {
        try {
          const response = await axios.post('/auth/wechat/unbind');
          return response.data;
        } catch (error) {
          return {
            success: false,
            message: error.response?.data?.message || '微信账号解绑失败'
          };
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

export { useAuthStore };
